// Typing-indicator timing.
//
// THROTTLE, not debounce — and that distinction is the whole point of this file.
//
// Debounce fires after the user STOPS. So a fast typist mid-sentence emits nothing at all,
// and the person waiting sees silence for exactly as long as someone is actively writing to
// them. That is backwards: the indicator's job is to say "words are coming", which is most
// true while the keys are moving.
//
// Throttle fires on the FIRST keystroke and then at most once per interval while typing
// continues, which is the behaviour every chat product actually ships.

/** Emit on the first keystroke, then at most once per this interval. */
export const TYPING_THROTTLE_MS = 3_000;

/** Send an explicit stop after this long without a keystroke. */
export const TYPING_IDLE_MS = 5_000;

/**
 * Receiver-side expiry.
 *
 * Deliberately LONGER than the sender's idle timeout. If they were equal, a refresh that
 * arrived a few hundred milliseconds late would blink the indicator off and on again, which
 * reads as a glitch rather than as someone typing.
 */
export const TYPING_EXPIRY_MS = 7_000;

export interface ITypingDecision {
	/** Whether to emit a typing event now. */
	emit: boolean;
	/** The timestamp to remember as the last emission, if one was made. */
	lastEmittedAt: number;
}

/**
 * "Nothing emitted yet" is `null`, deliberately, not `0`.
 *
 * Zero is a legitimate timestamp, so using it as the sentinel means the first emission sets
 * the marker back to "never" and the very next keystroke emits again. `Date.now()` is never
 * actually 0, so this would not have bitten in production — but a sentinel that can collide
 * with a real value is a bug waiting for a fake clock, and a test found exactly that.
 */
export type LastEmitted = number | null;

/**
 * Decide whether a keystroke should emit.
 *
 * Pure and clock-injected so the ladder can be asserted exactly, without fake timers.
 *
 * @param lastEmittedAt null when nothing has been emitted for this typing burst.
 */
export const shouldEmitTyping = (lastEmittedAt: LastEmitted, now: number): ITypingDecision => {
	// First keystroke of a burst: emit immediately. This is the difference between the
	// recipient seeing "typing…" at once and seeing it three seconds later.
	if (lastEmittedAt === null) return { emit: true, lastEmittedAt: now };

	if (now - lastEmittedAt >= TYPING_THROTTLE_MS) return { emit: true, lastEmittedAt: now };

	return { emit: false, lastEmittedAt };
};

/**
 * Whether the sender has been quiet long enough that an explicit stop should go out.
 * `null` means they never started, so there is nothing to stop.
 */
export const shouldEmitStop = (lastKeystrokeAt: LastEmitted, now: number): boolean =>
	lastKeystrokeAt !== null && now - lastKeystrokeAt >= TYPING_IDLE_MS;

/** Whether a received indicator has gone stale and should be cleared. */
export const hasTypingExpired = (receivedAt: number, now: number): boolean => now - receivedAt >= TYPING_EXPIRY_MS;

/**
 * Whether a typing event is worth sending at all.
 *
 * Never while offline: a typing event is worthless on arrival — by the time the queue drains
 * the person has long since stopped — and queueing them would push real messages further back
 * behind noise.
 */
export const canEmitTyping = (online: boolean, connected: boolean): boolean => online && connected;
